import { App, Button, Checkbox, Modal, Space, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import {
    TbAlertCircle,
    TbBan,
    TbChevronDown,
    TbChevronUp,
    TbCircleCheck,
    TbClockHour4,
    TbFile,
    TbPlayerPause,
    TbPlayerPlay,
    TbRefresh,
    TbX,
} from 'react-icons/tb'

import { classNames } from '@/Ship/Helpers/classNames'

import { formatBytes } from '../Helpers/diskFormat'
import { formatEta } from '../Helpers/diskUploadPlan'
import { uploadQueueStore as queue } from '../Stores/UploadQueueStore'

import type { DiskUploadState } from '../Contracts/DiskUpload'
import type { DiskUploadItem } from '../Stores/UploadQueueStore'
import type { ReactNode } from 'react'

import styles from './DiskUpload.module.css'

const STATE_LABEL: Record<DiskUploadState, string> = {
    queued: 'У черзі',
    uploading: 'Вантажиться',
    paused: 'Пауза',
    done: 'Готово',
    error: 'Помилка',
    cancelled: 'Скасовано',
    conflict: 'Назва зайнята — чекає на рішення',
    skipped: 'Пропущено',
    needsFile: 'Перервано перезавантаженням сторінки',
}

const STATE_ICON: Record<DiskUploadState, ReactNode> = {
    queued: <TbClockHour4 />,
    uploading: <TbFile />,
    paused: <TbPlayerPause />,
    done: <TbCircleCheck />,
    error: <TbAlertCircle />,
    cancelled: <TbBan />,
    conflict: <TbAlertCircle />,
    skipped: <TbBan />,
    needsFile: <TbPlayerPause />,
}

const ROW_CLASS: Partial<Record<DiskUploadState, string>> = {
    done: 'rowDone',
    error: 'rowError',
    paused: 'rowPaused',
    needsFile: 'rowNeedsFile',
}

const percentOf = (item: DiskUploadItem): number =>
    item.size === 0 ? (item.state === 'done' ? 100 : 0) : Math.min(100, Math.floor((item.uploadedBytes * 100) / item.size))

/** Рядок стану: скільки з скількох, швидкість і залишок часу — там, де вони мають сенс. */
const metaText = (item: DiskUploadItem): string => {
    if (item.state === 'error') return item.error ?? STATE_LABEL.error

    const parts = [STATE_LABEL[item.state]]
    const partial = item.uploadedBytes > 0 && item.uploadedBytes < item.size

    if (item.state === 'done' || item.state === 'queued') parts.push(formatBytes(item.size))
    else if (partial || item.state === 'uploading') {
        parts.push(`${formatBytes(item.uploadedBytes)} з ${formatBytes(item.size)}`)
    }

    if (item.state === 'uploading' && item.speed > 0) {
        parts.push(`${formatBytes(item.speed)}/с`)

        const eta = formatEta((item.size - item.uploadedBytes) / item.speed)
        if (eta !== '') parts.push(`залишилось ~${eta}`)
    }

    return parts.join(' · ')
}

const IconAction = ({ label, icon, onClick }: { label: string; icon: ReactNode; onClick: () => void }) => (
    <Tooltip title={label}>
        <Button type="text" size="small" icon={icon} aria-label={label} onClick={onClick} />
    </Tooltip>
)

const UploadRow = observer(({ item, onPick }: { item: DiskUploadItem; onPick: (id: string) => void }) => {
    const path = [item.target.parentName, item.relativePath].filter((part) => part !== '').join(' / ')
    const showBar = ['uploading', 'paused', 'needsFile'].includes(item.state)
    const rowClass = ROW_CLASS[item.state]

    return (
        <li
            className={classNames(styles['row'], rowClass !== undefined && styles[rowClass])}
            data-testid="disk-upload-item"
            data-state={item.state}
            data-name={item.name}
        >
            <span className={classNames(styles['rowIcon'])} aria-hidden>
                {STATE_ICON[item.state]}
            </span>
            <div className={classNames(styles['rowMain'])}>
                <span className={classNames(styles['rowName'])} title={item.name}>
                    {item.name}
                </span>
                {path !== '' && (
                    <span className={classNames(styles['rowPath'])} title={path}>
                        у «{path}»
                    </span>
                )}
                <span className={classNames(styles['rowMeta'])} data-testid="disk-upload-meta">
                    {metaText(item)}
                </span>
                {showBar && (
                    <div
                        className={classNames(styles['rowBar'])}
                        role="progressbar"
                        aria-valuenow={percentOf(item)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`Поступ «${item.name}»`}
                    >
                        <div className={classNames(styles['rowBarFill'])} style={{ width: `${percentOf(item)}%` }} />
                    </div>
                )}
                {item.state === 'needsFile' && (
                    <Button size="small" className={classNames(styles['rowPick'])} onClick={() => onPick(item.id)}>
                        Продовжити — виберіть той самий файл
                    </Button>
                )}
            </div>
            <div className={classNames(styles['rowActions'])}>
                {(item.state === 'uploading' || item.state === 'queued') && (
                    <IconAction label="Пауза" icon={<TbPlayerPause />} onClick={() => queue.pause(item.id)} />
                )}
                {item.state === 'paused' && (
                    <IconAction label="Продовжити" icon={<TbPlayerPlay />} onClick={() => queue.resume(item.id)} />
                )}
                {item.state === 'error' && item.file !== null && (
                    <IconAction label="Повторити" icon={<TbRefresh />} onClick={() => queue.retry(item.id)} />
                )}
                {!['done', 'cancelled', 'skipped'].includes(item.state) && (
                    <IconAction label="Скасувати" icon={<TbX />} onClick={() => queue.cancel(item.id)} />
                )}
            </div>
        </li>
    )
})

/** Заголовок панелі: що відбувається загалом. */
const headline = (): { title: string; sub: string } => {
    const { done, total, bytes, totalBytes, percent } = queue.progress
    const items = queue.items
    const count = (state: DiskUploadState): number => items.filter((item) => item.state === state).length
    const speed = items.filter((item) => item.state === 'uploading').reduce((sum, item) => sum + item.speed, 0)

    if (queue.isBusy) {
        const eta = speed > 0 ? formatEta((totalBytes - bytes) / speed) : ''
        const sub = [`${percent} %`, speed > 0 ? `${formatBytes(speed)}/с` : '', eta !== '' ? `~${eta}` : '']

        return { title: `Завантаження: ${done} з ${total}`, sub: sub.filter((part) => part !== '').join(' · ') }
    }

    if (count('conflict') > 0) return { title: 'Назва зайнята', sub: 'Оберіть, що зробити з файлом' }
    if (count('needsFile') > 0) {
        return { title: `Не завершено: ${count('needsFile')}`, sub: 'Оберіть ті самі файли, щоб докачати решту' }
    }
    if (count('paused') > 0) return { title: `На паузі: ${count('paused')}`, sub: `${percent} %` }

    const errors = count('error')
    const dropped = count('skipped') + count('cancelled')

    if (total === 0) {
        return { title: 'Нічого не завантажено', sub: `Пропущено чи скасовано: ${dropped}` }
    }

    return {
        title: `Завантажено: ${done} з ${total}`,
        sub: errors > 0 ? `Помилок: ${errors}` : 'Усі файли на диску',
    }
}

/**
 * Діалог конфлікту назв: обидва, нова версія або пропустити; «Застосувати до
 * всіх». Основна дія — «Зберегти обидва» (`DISK-005` U-27): версій у UI ще
 * немає, тож заміна для людини виглядала б як перезапис, хоч бек і тримає
 * попередні версії (`versionsToKeep` простору). Коли з'являться «Версії» —
 * повернути «Замінити» основною.
 */
const ConflictModal = observer(() => {
    const item = queue.pendingConflict
    const [applyAll, setApplyAll] = useState(false)
    const more = item ? queue.conflictsInBatch(item) : 0

    useEffect(() => setApplyAll(false), [item?.batchId])

    if (item === null) return null

    const answer = (choice: 'version' | 'keep_both' | 'skip') => queue.resolveConflict(item.id, choice, applyAll)

    return (
        <Modal
            open
            width={660}
            title="Файл із такою назвою вже є"
            onCancel={() => answer('skip')}
            destroyOnHidden
            footer={
                <Space wrap style={{ justifyContent: 'flex-end' }}>
                    <Button onClick={() => answer('skip')}>Пропустити</Button>
                    <Button onClick={() => answer('version')}>Замінити новою версією</Button>
                    <Button type="primary" onClick={() => answer('keep_both')} autoFocus>
                        Зберегти обидва
                    </Button>
                </Space>
            }
        >
            <p>
                У папці «{item.target.parentName}» вже є «{item.name}». «Зберегти обидва» покладе новий файл поруч під
                назвою з номером.
            </p>
            <p className={classNames(styles['conflictNote'])}>
                «Замінити новою версією» зробить новий файл наступною версією наявного. Попередня версія збережеться,
                хоча переглянути й відновити версії в диску поки не можна.
            </p>
            {more > 0 && (
                <Checkbox checked={applyAll} onChange={(event) => setApplyAll(event.target.checked)}>
                    Застосувати до всіх (ще {more})
                </Checkbox>
            )}
        </Modal>
    )
})

/**
 * Панель менеджера завантажень праворуч унизу (`DISK-001` §6, Д1.10).
 * Стоїть у каркасі застосунку, тож лишається на екрані й після переходу
 * з диска в інший модуль; після перезавантаження показує незавершені
 * завантаження з кнопкою вибору того самого файлу.
 */
export const DiskUploadHost = observer(() => {
    const { message } = App.useApp()
    const picker = useRef<HTMLInputElement>(null)
    const [pickingId, setPickingId] = useState<string | null>(null)
    const busy = queue.isBusy

    useEffect(() => {
        void queue.restore()
    }, [])

    // Поки щось вантажиться, вихід зі сторінки перериває завантаження — питаємо
    useEffect(() => {
        if (!busy) return undefined

        const warn = (event: BeforeUnloadEvent): void => {
            event.preventDefault()
            event.returnValue = ''
        }

        window.addEventListener('beforeunload', warn)

        return () => window.removeEventListener('beforeunload', warn)
    }, [busy])

    const pick = (id: string): void => {
        setPickingId(id)
        picker.current?.click()
    }

    const onPicked = (files: FileList | null): void => {
        const file = files?.[0]

        if (file && pickingId !== null) {
            const mismatch = queue.attachFile(pickingId, file)

            if (mismatch !== null) void message.error({ content: mismatch, duration: 6 })
        }

        setPickingId(null)
    }

    if (!queue.isVisible) return <ConflictModal />

    const { title, sub } = headline()
    const collapsed = queue.collapsed
    const finishedAll = !busy && queue.items.every((item) => ['done', 'cancelled', 'skipped', 'error'].includes(item.state))

    return (
        <>
            <section className={classNames(styles['panel'])} aria-label="Завантаження" data-testid="disk-upload-panel">
                <div className={classNames(styles['head'])}>
                    <button
                        type="button"
                        className={classNames(styles['headText'])}
                        onClick={() => queue.toggleCollapsed()}
                        aria-expanded={!collapsed}
                    >
                        <span className={classNames(styles['headTitle'])} data-testid="disk-upload-title">
                            {title}
                        </span>
                        <span className={classNames(styles['headSub'])}>{sub}</span>
                    </button>
                    <IconAction
                        label={collapsed ? 'Розгорнути' : 'Згорнути'}
                        icon={collapsed ? <TbChevronUp /> : <TbChevronDown />}
                        onClick={() => queue.toggleCollapsed()}
                    />
                    {finishedAll && <IconAction label="Закрити" icon={<TbX />} onClick={() => queue.dismiss()} />}
                </div>
                <div className={classNames(styles['totalBar'])} aria-hidden>
                    <div className={classNames(styles['totalBarFill'])} style={{ width: `${queue.progress.percent}%` }} />
                </div>
                {!collapsed && (
                    <ul className={classNames(styles['list'])}>
                        {queue.items.map((item) => (
                            <UploadRow key={item.id} item={item} onPick={pick} />
                        ))}
                    </ul>
                )}
                {!collapsed && queue.hasFinished && (
                    <div className={classNames(styles['foot'])}>
                        <Button size="small" type="text" onClick={() => queue.clearFinished()}>
                            Очистити завершені
                        </Button>
                    </div>
                )}
                <input
                    ref={picker}
                    type="file"
                    hidden
                    onChange={(event) => {
                        onPicked(event.target.files)
                        event.target.value = ''
                    }}
                    data-testid="disk-upload-resume-input"
                />
            </section>
            <ConflictModal />
        </>
    )
})
